'use client';

import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { motion } from 'framer-motion';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { BaseLineUp2 } from '@/app/components/icons/BaseLineUp2';
import { useIsScreen } from '@/app/hooks';
import { cn } from '@/app/utils';
import { useDeferredMediaRender } from '../useDeferredMediaRender';
import { CarouselItem } from './_components/CarouselItem';
import { DetailCard } from './_components/DetailCard';
import { TitleSection } from './_components/TitleSection';
import { fadeInUpVariants } from './animation';
import type { VideoResource } from './constants';

interface ViduAbilityProps {
  title: string;
  description: string;
  button?: string;
  buttonRedirectUrl?: string;
  videoResource: VideoResource[];
  detail?: {
    id: string;
    title: string;
    description: string;
    button: string;
    icon: React.ReactNode;
    backgroundImage: string;
    redirectUrl: string;
  }[];
}

const MemoizedCarouselItem = memo(CarouselItem);

export const ViduAbilityItem = ({
  title,
  description,
  button,
  buttonRedirectUrl,
  videoResource,
  detail,
}: ViduAbilityProps) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { ref: mediaGateRef, shouldRenderMedia } = useDeferredMediaRender<HTMLDivElement>();
  const { isMdScreen } = useIsScreen();

  // 为PC端和移动端创建独立的轮播实例
  const autoplayOptions = { delay: 3750, playOnInit: false }; // 初始不自动播放
  const autoplayRef = useRef<ReturnType<typeof Autoplay>>(Autoplay(autoplayOptions));

  const canLoop = videoResource.length > 1;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: canLoop }, [autoplayRef.current]);

  // 监听组件可见性和处理轮播实例
  useEffect(() => {
    if (!sectionRef.current || !emblaApi) return;

    // 处理自动播放切换
    const handleAutoplayToggle = (api: typeof emblaApi, isIntersecting: boolean) => {
      if (!api) return;
      const autoplay = api.plugins().autoplay;
      if (!autoplay) return;

      if (isIntersecting) {
        if (canLoop) autoplay.play();
        setSelectedIndex(api.selectedScrollSnap());
      } else {
        autoplay.stop();
      }
    };

    // 设置观察器
    // 整块能力区很高：threshold 过大时用户刚滚到版块常不满足「50% 在视口内」，
    // selectedIndex 会一直是 -1，CarouselItem 不挂载 <source>，视频永远不出现。
    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries[0].isIntersecting;

        // 根据可见性控制自动播放
        handleAutoplayToggle(emblaApi, isIntersecting);
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
    );

    observer.observe(sectionRef.current);

    // 处理轮播同步

    const onSelect = () => {
      const index = emblaApi?.selectedScrollSnap() || 0;
      setSelectedIndex(index);
    };

    emblaApi.on('reInit', onSelect).on('select', onSelect);

    // 清理函数
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }

      // 停止自动播放并移除事件监听
      if (!emblaApi) return;
      const autoplay = emblaApi.plugins().autoplay;
      if (autoplay) autoplay.stop();

      emblaApi.off('reInit', onSelect).off('select', onSelect);
    };
  }, [emblaApi]);

  // 推迟挂载媒体后轮播才出现：首帧 selectedIndex 可能仍为 -1，需与 Embla 当前页对齐才能挂上 <source>
  useEffect(() => {
    if (!shouldRenderMedia || !emblaApi) return;
    setSelectedIndex((prev) => (prev < 0 ? emblaApi.selectedScrollSnap() : prev));
  }, [shouldRenderMedia, emblaApi]);

  const hasMirrorReflection = videoResource[0]?.poster.length > 0;

  const Carousels = useMemo(() => {
    if (isMdScreen) {
      if (!shouldRenderMedia) {
        return (
          <div className="flex w-full gap-8">
            <div className="w-full">
              <div className="aspect-video w-full rounded-12 bg-system-white06 md:rounded-none" />
              {hasMirrorReflection && <div className="h-16 w-full" />}
            </div>
          </div>
        );
      }

      return (
        <div className="flex gap-8">
          <div className="relative overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {videoResource.map((item, index) => (
                <div
                  key={`mobile-ability-${item.url}-${index}`}
                  className="relative flex min-w-full shrink-0 grow-0 basis-full flex-col items-center justify-center p-4 md:p-0"
                >
                  <MemoizedCarouselItem {...item} isSelected={index === selectedIndex} />
                </div>
              ))}
            </div>
            {hasMirrorReflection && (
              <div className="pointer-events-none absolute bottom-0 z-10 h-16 w-full bg-gradient-to-t from-black to-transparent">
                {/* 左边渐变 */}
                <div className="absolute top-0 left-0 h-full w-10 bg-gradient-to-r from-black to-transparent" />
                {/* 右边渐变 */}
                <div className="absolute top-0 right-0 h-full w-10 bg-gradient-to-l from-black to-transparent" />
                {/* 底部渐变 */}
                <div className="absolute bottom-0 left-0 h-16 w-full bg-gradient-to-t from-black to-transparent" />
              </div>
            )}
          </div>
        </div>
      );
    }
    if (!shouldRenderMedia) {
      return (
        <div className="mt-8 w-full max-w-[1000px] overflow-hidden rounded-12">
          <div className="flex items-center justify-center gap-8">
            {canLoop && <div className="h-[228px] w-10 shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="aspect-video w-full bg-system-white06" />
              {hasMirrorReflection && <div className="h-40 w-full" />}
            </div>
            {canLoop && <div className="h-[228px] w-10 shrink-0" />}
          </div>
        </div>
      );
    }

    return (
      <div className="mt-8 w-full max-w-[1000px] overflow-hidden rounded-12">
        <div className="flex items-center justify-center gap-8">
          {canLoop && (
            <div
              className={cn('flex h-[228px] cursor-pointer', hasMirrorReflection ? 'items-start' : 'items-center')}
              onClick={() => emblaApi?.scrollPrev()}
            >
              <BaseLineLeft className="size-10" />
            </div>
          )}

          <div className="relative overflow-hidden rounded-12" ref={emblaRef}>
            <div className="flex">
              {videoResource.map((item, index) => (
                <div
                  key={`pc-ability-${item.url}-${index}`}
                  className="relative mx-4 flex min-w-full shrink-0 grow-0 basis-full flex-col items-center justify-center"
                >
                  <MemoizedCarouselItem {...item} isSelected={index === selectedIndex} />
                </div>
              ))}
            </div>
            {hasMirrorReflection && (
              <div className="pointer-events-none absolute bottom-0 z-10 h-40 w-full bg-gradient-to-t from-black to-transparent">
                {/* 左边渐变 */}
                <div className="absolute top-0 left-0 h-full w-40 bg-gradient-to-r from-black to-transparent" />
                {/* 右边渐变 */}
                <div className="absolute top-0 right-0 h-full w-40 bg-gradient-to-l from-black to-transparent" />
                {/* 底部渐变 */}
                <div className="absolute bottom-0 left-0 h-40 w-full bg-gradient-to-t from-black to-transparent" />
              </div>
            )}
          </div>

          {canLoop && (
            <div
              className={cn('flex h-[228px] cursor-pointer', hasMirrorReflection ? 'items-start' : 'items-center')}
              onClick={() => emblaApi?.scrollNext()}
            >
              <BaseLineRight className="size-10" />
            </div>
          )}
        </div>
      </div>
    );
  }, [isMdScreen, videoResource, selectedIndex, shouldRenderMedia, canLoop, hasMirrorReflection]);

  // 详情卡片区域
  const renderDetailSection = useMemo(() => {
    if (!detail?.length) return null;

    if (isMdScreen) {
      return (
        <div className="mx-4 hidden flex-col gap-5 md:flex">
          {detail.map((item) => (
            <DetailCard
              key={`mobile-detail-${item.id}`}
              item={item}
              isMobileScreen={true}
              renderBackgroundImage={shouldRenderMedia}
            />
          ))}
        </div>
      );
    }
    return (
      <motion.div
        className="flex w-full items-center justify-center gap-[20px] md:hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={fadeInUpVariants({ hiddenY: 40 })}
        custom={2}
      >
        {detail.map((item) => (
          <DetailCard key={`pc-detail-${item.id}`} item={item} renderBackgroundImage={shouldRenderMedia} />
        ))}
      </motion.div>
    );
  }, [detail, isMdScreen, shouldRenderMedia]);

  return (
    <div
      className={cn(
        'flex flex-col items-center px-20 pt-48 md:px-0 md:py-0 lg:px-[29px] lg:pt-[123px]',
        hasMirrorReflection ? 'pb-16 lg:pb-[40px]' : 'pb-0 lg:pb-0',
      )}
      ref={(node) => {
        sectionRef.current = node;
        mediaGateRef.current = node;
      }}
    >
      {/* 头部介绍信息区 */}
      <TitleSection title={title} description={description} />

      {/* 轮播组件 */}
      {Carousels}

      {/* 行动按钮 */}
      {button && buttonRedirectUrl && (
        <a
          href={buttonRedirectUrl}
          className="z-10 mt-10 flex w-fit cursor-pointer items-center gap-2 rounded-full border-[2px] border-white border-solid px-6 py-3 text-[14px] md:mt-8"
        >
          {button}
          <BaseLineUp2 className="size-5 rotate-90" />
        </a>
      )}

      {/* 底部详情介绍区 */}
      {renderDetailSection}
    </div>
  );
};
